import type { ReactNode } from 'react'
import { PexIcon } from './icons/PexIcon'
import type { PexIconName } from './icons/pex-icon-registry'
import { useT } from '../i18n'
import { dashboardCardsI18n } from '../i18n/shared/dashboard-cards'

// Shared card primitives for the 3 V1 dashboards (admin/seller/supply).
// Surface, border, radius, and elevation come from the global card tokens.

// ---------- Filter bar ----------

export function FilterBar({
  liveLabel,
  rightSlot,
}: {
  liveLabel: string
  rightSlot?: ReactNode
}) {
  const t = useT(dashboardCardsI18n)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip label={t.filters.today} />
        <FilterChip label={t.filters.allChannels} />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] px-2.5 py-1 text-xs font-medium text-[var(--color-success)] ring-1 ring-[var(--color-success)]/30">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          {liveLabel}
        </span>
      </div>
      {rightSlot && <div className="text-xs">{rightSlot}</div>}
    </div>
  )
}

function FilterChip({ label }: { label: string }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-md bg-[var(--color-surface)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] ring-1 ring-[var(--color-border)] transition-all hover:ring-[var(--color-border-strong)] hover:shadow-sm"
    >
      {label}
      <span className="text-[var(--color-text-muted)]">▾</span>
    </button>
  )
}

// ---------- Info card (small) for right side of filter bar ----------

export function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 shadow-[var(--card-shadow)]">
      <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </div>
      <div className="text-sm font-semibold text-[var(--color-text)] tabular-nums">{value}</div>
    </div>
  )
}

// ---------- Metric card ----------

export type Trend = {
  direction: 'up' | 'down'
  percent: number
}

export function MetricCard({
  label,
  value,
  trend,
  sparkline,
}: {
  label: string
  value: string
  trend?: Trend
  sparkline?: number[]
}) {
  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-[var(--card-shadow)] transition-shadow hover:shadow-[var(--card-interactive-shadow)]">
      <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-[var(--color-text)] tabular-nums">
          {value}
        </span>
        {trend && <TrendChip trend={trend} />}
      </div>
      {sparkline && <Sparkline points={sparkline} direction={trend?.direction} />}
    </div>
  )
}

function TrendChip({ trend }: { trend: Trend }) {
  const positive = trend.direction === 'up'
  const cls = positive
    ? 'bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] text-[var(--color-success)]'
    : 'bg-[color-mix(in_srgb,var(--color-danger)_12%,var(--color-surface))] text-[var(--color-danger)]'
  const arrow = positive ? '↗' : '↘'
  return (
    <span
      className={
        'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium ' +
        cls
      }
    >
      <span>{arrow}</span>
      <span className="tabular-nums">{trend.percent}%</span>
    </span>
  )
}

function Sparkline({
  points,
  direction = 'up',
}: {
  points: number[]
  direction?: 'up' | 'down'
}) {
  if (points.length < 2) return null
  const width = 100
  const height = 28
  const padding = 2
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const stepX = (width - padding * 2) / (points.length - 1)
  const coords = points.map((p, i) => {
    const x = padding + i * stepX
    const y = padding + (1 - (p - min) / range) * (height - padding * 2)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })
  const stroke = direction === 'down' ? '#f43f5e' : '#3b82f6'
  return (
    <svg
      className="mt-3 h-7 w-full"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={coords.join(' ')}
      />
    </svg>
  )
}

// ---------- Announcement banner ----------

export function AnnouncementBanner({
  eyebrow,
  title,
  body,
  ctaLabel,
  onCta,
}: {
  eyebrow?: string
  title: string
  body: string
  ctaLabel?: string
  onCta?: () => void
}) {
  return (
    <div className="relative overflow-hidden rounded-[var(--card-radius)] border border-slate-900 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-[var(--card-shadow)]">
      {/* Decorative gradient blob — purely CSS, no icon import */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-30 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(99,102,241,0.55) 0%, rgba(15,23,42,0) 70%)',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full opacity-25 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(16,185,129,0.5) 0%, rgba(15,23,42,0) 70%)',
        }}
        aria-hidden
      />
      <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          {eyebrow && (
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-300">
              {eyebrow}
            </div>
          )}
          <h3 className="mt-1 text-base font-semibold tracking-tight text-white">
            {title}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{body}</p>
          {ctaLabel && (
            <button
              type="button"
              onClick={onCta}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/15"
            >
              {ctaLabel}
              <PexIcon name="caretRight" className="h-3.5 w-3.5" size="xs" tone="inherit" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------- Quick action card ----------

export function QuickActionCard({
  icon,
  label,
  tone = 'default',
  onClick,
}: {
  icon: PexIconName
  label: string
  tone?: 'default' | 'warning'
  onClick?: () => void
}) {
  const iconWrap =
    tone === 'warning'
      ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
      : 'bg-[var(--color-surface-muted)] text-[var(--color-text)] ring-1 ring-[var(--color-border)]'
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-4 text-left shadow-[var(--card-shadow)] transition-shadow hover:shadow-[var(--card-interactive-shadow)]"
    >
      <span
        className={
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-md ' + iconWrap
        }
      >
        <PexIcon
          name={icon}
          size="sm"
          tone={tone === 'warning' ? 'warning' : 'strong'}
        />
      </span>
      <span className="flex-1 text-sm font-medium text-[var(--color-text)]">{label}</span>
      <PexIcon name="caretRight" className="transition-colors group-hover:text-[var(--color-text)]" size="xs" tone="muted" />
    </button>
  )
}

// ---------- Body card (generic Shopify-style container) ----------

export function BodyCard({
  title,
  description,
  children,
}: {
  title?: string
  description?: string
  children: ReactNode
}) {
  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-[var(--card-shadow)]">
      {title && (
        <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
      )}
      {description && (
        <p className="mt-1 text-sm leading-relaxed text-[var(--color-text-muted)]">{description}</p>
      )}
      {children && <div className={title || description ? 'mt-3' : ''}>{children}</div>}
    </div>
  )
}

// ---------- Section header (used between row groups) ----------

export function SectionHeader({
  title,
  hint,
  right,
}: {
  title: string
  hint?: string
  right?: ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        {title}
        {hint && (
          <span className="ml-2 text-xs font-normal normal-case text-[var(--color-text-muted)]">
            {hint}
          </span>
        )}
      </h2>
      {right}
    </div>
  )
}
